import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

function mapsKey() {
  if (typeof document === "undefined" || !document.documentElement) {
    return "";
  }
  const key = document.documentElement.getAttribute("data-maps-key");
  return typeof key === "string" ? key.trim() : "";
}

function legacyType(p) {
  if (p.osm_key == "highway") {
    return "street";
  }
  if (p.osm_key == "boundary" || (p.osm_key == "place" && p.osm_value == "suburb")) {
    return "district";
  }
  if (p.osm_key == "place" && ["city", "town", "village", "hamlet"].indexOf(p.osm_value) !== -1) {
    return "city";
  }
  return p.housenumber ? "house" : "";
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.requestSeq = 0;
    this.source = null;
  }

  cancelAutocomplete() {
    this.requestSeq++;
    if (this.source != null) {
      this.source.cancel();
      this.source = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _geoaddress(feature) {
    if (!feature || !feature.properties || !feature.geometry || !Array.isArray(feature.geometry.coordinates)) {
      return null;
    }

    // Koordinaten kommen als [lng, lat]
    const lng = Number(feature.geometry.coordinates[0]);
    const lat = Number(feature.geometry.coordinates[1]);
    if (!isFinite(lat) || !isFinite(lng)) {
      return null;
    }

    const p = feature.properties;
    const type = p.type || legacyType(p);
    const geoaddress = {};

    const street = p.street || (type == "street" ? p.name : null);
    if (street) {
      geoaddress["street"] = street;
      if (p.housenumber) {
        geoaddress["number"] = p.housenumber;
      }
    }

    if (p.postcode) {
      geoaddress["zip"] = p.postcode;
    }

    if (type == "city") {
      geoaddress["city"] = p.name || p.city;
    } else if (p.city) {
      geoaddress["city"] = p.city;
    }

    if (type == "district") {
      const district = p.name || p.district;
      if (district && district != geoaddress["city"]) {
        geoaddress["district"] = district;
      }
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = p.state;
    geoaddress["country"] = p.country;

    geoaddress["text"] = this._geoaddressText(geoaddress);
    if (!geoaddress["text"]) {
      // ohne Orts-/Straßenangabe den Feature-Namen als Beschriftung verwenden
      geoaddress["text"] = [p.name, p.state, p.country].filter(v => !!v).join(", ");
    }
    if (!geoaddress["text"]) {
      return null;
    }

    return geoaddress;
  }

  _features(res) {
    const features = res && res.data && Array.isArray(res.data.features) ? res.data.features : [];
    const results = [];
    features.forEach(f => {
      const geoaddress = this._geoaddress(f);
      if (geoaddress) {
        results.push(geoaddress);
      }
    });
    return results;
  }

  // Liefert ein Array mit Treffern oder null, wenn die Anfrage abgebrochen
  // oder von einer neueren Suche überholt wurde.
  autocomplete(q) {
    this.cancelAutocomplete();
    const requestId = this.requestSeq;

    const query = typeof q === "string" ? q.trim() : "";
    const key = mapsKey();
    if (!query) {
      return Promise.resolve([]);
    }
    if (!key) {
      console.warn("Geocoding nicht verfügbar: kein Maps-Key");
      return Promise.resolve([]);
    }

    const source = this.axios.CancelToken.source();
    this.source = source;

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("key", key);

    return this.axios.get(SEARCH_URL, {
      params,
      cancelToken: source.token,
      timeout: REQUEST_TIMEOUT
    }).then(res => {
      if (requestId !== this.requestSeq) {
        return null;
      }
      return this._features(res);
    }).catch(err => {
      if (requestId !== this.requestSeq || this.axios.isCancel(err)) {
        return null;
      }
      console.warn("Geocoding fehlgeschlagen");
      return [];
    }).then(result => {
      if (this.source === source) {
        this.source = null;
      }
      return result;
    });
  }

  // Liefert eine Adresse oder null.
  reverse(lat, lng) {
    const latNum = Number(lat);
    const lonNum = Number(lng);
    if (!isFinite(latNum) || !isFinite(lonNum) || latNum < -90 || latNum > 90 || lonNum < -180 || lonNum > 180) {
      return Promise.resolve(null);
    }

    const key = mapsKey();
    if (!key) {
      console.warn("Reverse-Geocoding nicht verfügbar: kein Maps-Key");
      return Promise.resolve(null);
    }

    const params = new URLSearchParams();
    params.append("lat", String(latNum));
    params.append("lon", String(lonNum));
    params.append("lang", "de");
    params.append("key", key);

    return this.axios.get(REVERSE_URL, {
      params,
      timeout: REQUEST_TIMEOUT
    }).then(res => {
      const results = this._features(res);
      return results.length > 0 ? results[0] : null;
    }).catch(() => {
      console.warn("Reverse-Geocoding fehlgeschlagen");
      return null;
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
